import * as Sentry from "@sentry/nextjs";

/**
 * Emit a Sentry EVENT for a user-impacting state that produces no organic
 * error — a watchdog signal, not an exception.
 *
 * Why this exists: states like "stuck on the splash screen" or "parked after
 * exhausting signin redirects" never throw, so nothing reaches Sentry and
 * nobody gets the Slack alert. The June 2026 frozen-splash incident (failed
 * Keycloak error callback) was invisible for exactly this reason — the auth
 * gate returned "wait" forever and the only signal was a user report.
 *
 * This is the ALERTING tier — every call here becomes a Sentry issue, so use
 * it only when a human should look. A state that is self-healing, or is the
 * expected consequence of a constraint we don't control, belongs in
 * `noteSilentSignal` (same console/Kibana/replay trail, no issue).
 *
 * console.error/warn alone is NOT enough: the Sentry SDK records console
 * output as a breadcrumb, which only ships if some later event fires.
 * This helper does both — `Sentry.captureMessage` for the alertable event
 * and a console line so the failure is visible in the Mixpanel
 * session-replay console panel and (warn/error) shipped as an OTel log.
 *
 * Conventions (see .claude/docs/web-sentry-error-tracking.md):
 * - `message` carries a category prefix: `[Boot]` for app-start watchdogs,
 *   `[Auth]` for auth-flow states.
 * - `fingerprint` is a stable kebab-case key so each failure class groups
 *   into ONE Sentry issue (Slack alerts fire per issue, not per event).
 * - Tags/extra must be PII-safe: derived booleans and fixed-vocabulary
 *   codes only — never raw URLs, query strings, or user input. Note that
 *   `sharedBeforeSend` does NOT strip arbitrary extras.
 */
export function reportSilentFailure({
  message,
  level = "error",
  fingerprint,
  tags,
  extra,
}: {
  message: string;
  level?: "warning" | "error";
  /** Stable grouping key, e.g. "boot-splash-stuck". */
  fingerprint: string;
  tags?: Record<string, string>;
  extra?: Record<string, string | number | boolean>;
}): void {
  const consoleFn = level === "error" ? console.error : console.warn;
  consoleFn(`[silent-failure] ${message}`, JSON.stringify({ tags, extra }));

  Sentry.captureMessage(message, {
    level,
    fingerprint: [fingerprint],
    tags: { ...tags, "silent.failure": "true" },
    extra,
  });
}
